/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-03 15:58:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';

import {
  Card,
  DataTable,
  SkeletonDisplayText,
  Pagination,
  Icon,
  TextStyle,
  Tooltip
} from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import { CaretDownMinor, CaretUpMinor } from '@shopify/polaris-icons';
import { getUTCDate } from 'src/helpers/date-time-helper';
import { buildQueryKey, findExecutionId } from 'src/helpers/analytics-query-execution-id-helper';
import Filter from 'src/components/containers/analytics-v2/filter';
import {
  QUERY_EXECUTION_KEY,
  TOP_ITEM_IN_DETAIL,
  NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL,
  MAP_SORT_VALUE,
  SORT_VALUE,
  COLLECTION_ANALYTIC_METRIC,
  FEATURES
} from 'src/constants/analytics';
import { formatNumberInThousand } from 'src/helpers';
import * as AnalyticsActions from 'src/stores/actions/analytics-v2-actions';
import * as AnalyticsQueryExecutionIdActions from 'src/stores/actions/analytics-query-execution-id-actions';
import CollectionAnalyticActionList from 'src/components/views/analytics-v2/collection-analytic-action-list';
import { onAbortApiAnalytic } from 'src/helpers/analytics-v2-helpers';

type Props = {
  performActionSurvey: () => void
};
type ComposedProps = Props & WithI18nProps;
type State = {
  data: any[],
  perPage: number,
  page: number,
  hasNext: boolean,
  hasPrevious: boolean,
  rows: any[],
  data: any[],
  totalPage: number,
  directionSort: string
};

const fromDateStateName = 'collectionAnalyticsFromDate';
const toDateStateName = 'collectionAnalyticsToDate';
const compareStateName = 'collectionAnalyticsCompare';

const orderHeaders = ['filterOptionValue', 'totalClicks'];

class FilterOptionValueAnalytics extends Component<ComposedProps, State> {
  state = {
    perPage: 10,
    hasPrevious: false,
    hasNext: false,
    rows: [],
    data: [],
    page: 1,
    totalPage: 0,
    directionSort: 'desc',
    currenCollectionId: 'all'
  };

  componentDidMount() {
    const { collectionTop, fromDate, toDate, compare } = this.props;
    const { perPage } = this.state;

    const hasNext = _.size(collectionTop) > perPage;
    this.setState({ rows: collectionTop, hasNext });

    if (fromDate && toDate) {
      // Get from date, to date with store timezone
      const fromDateUTC = getUTCDate(fromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(toDate, 24, 0, 0, 0);
      this.getCollectionTopFilterOptionValue(fromDateUTC, toDateUTC, compare);
    }
  }

  componentWillReceiveProps(nextProps) {
    const { fromDate, toDate, compare } = this.props;
    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      collectionTopFilterOptionValue: nextCollectionTopFilterOptionValue
    } = nextProps;

    // Check changing filter
    if (fromDate !== nextFromDate || toDate !== nextToDate || compare !== nextCompare) {
      // Get from date, to date with store timezone
      const fromDateUTC = getUTCDate(nextFromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(nextToDate, 24, 0, 0, 0);

      // Reset page when change date filter
      this.setState({
        page: 1
      });

      onAbortApiAnalytic();
      setTimeout(() => {
        this.getCollectionTopFilterOptionValue(fromDateUTC, toDateUTC, nextCompare);
      }, 500);
    }

    if (!_.isEmpty(nextCollectionTopFilterOptionValue)) {
      const newTotalPage = Math.ceil(
        nextCollectionTopFilterOptionValue.length / NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL
      );
      this.setState({
        totalPage: newTotalPage
      });
    }

    this.paginateData(this.state.page, nextCollectionTopFilterOptionValue);

    // Update execution ids
    this.processFilterOptionExecutionId(this.props, nextProps);
  }

  paginateData = (page, data) => {
    this.setState({
      data: data.slice(
        (page - 1) * NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL,
        page * NUMBER_ITEMS_IN_NEW_ANALYTICS_DETAIL
      )
    });
  };

  buildOptions = () => {
    const { collectionTopFilterOptionValueLoading } = this.props;
    const { data } = this.state;
    const result = [];
    if (collectionTopFilterOptionValueLoading) {
      [...Array(5)].forEach(() =>
        result.push([<SkeletonDisplayText size="small" />, <SkeletonDisplayText size="small" />])
      );
    } else {
      const maxLengthFilterOptionValue = data.reduce((result, item) => {
        if (_.get(item, 'filterOptionLabel.length', 0) > result)
          return item.filterOptionLabel.length;
        return result;
      }, 0);
      data.forEach((item, i) => {
        const { totalClicks, filterOptionValue, filterOptionLabel } = item;
        result.push([
          <div className="filter-option">
            <div
              className="filter-option-label"
              style={{ '--width': `${maxLengthFilterOptionValue * 8}px` }}>
              {filterOptionLabel}
            </div>
            <div className="filter-option-value">
              {filterOptionValue.includes('class=') ? (
                <span
                  dangerouslySetInnerHTML={{
                    __html: filterOptionValue
                  }}></span>
              ) : (
                filterOptionValue
              )}
            </div>
          </div>,
          <div className="cell-value-table">{formatNumberInThousand(totalClicks)}</div>
        ]);
      });
    }
    return result;
  };

  handleSort = (headingIndex: number, direction: string) => {
    const { sortData } = this.props;
    this.setState({
      directionSort: MAP_SORT_VALUE[direction]
    });

    sortData(
      'collectionTopFilterOptionValue',
      orderHeaders[headingIndex],
      MAP_SORT_VALUE[direction]
    );
  };

  nextPage = () => {
    const { page, totalPage } = this.state;
    const { collectionTopFilterOptionValue } = this.props;
    if (page === totalPage) {
      return;
    }

    this.paginateData(page + 1, collectionTopFilterOptionValue);
    this.setState({
      page: page + 1
    });
  };

  previousPage = () => {
    const { page } = this.state;
    const { collectionTopFilterOptionValue } = this.props;
    if (page === 1) {
      return;
    }

    this.paginateData(page - 1, collectionTopFilterOptionValue);
    this.setState({
      page: page - 1
    });
  };

  getCollectionTopFilterOptionValue = (fromDate, toDate, compare, collectionId = 'all') => {
    const { executionIds } = this.props;
    compare = false;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare, collectionId });
    const queryExecutionKey = QUERY_EXECUTION_KEY.COLLECTION_TOP_FILTER_OPTION_VALUE;
    // Get execution id if existing
    const queryExecutionId = findExecutionId(queryKey, queryExecutionKey, executionIds);

    this.props.getCollectionTopFilterOptionValue(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      TOP_ITEM_IN_DETAIL,
      collectionId
    );
  };

  processFilterOptionExecutionId = (props, nextProps) => {
    const { collectionTopFilterOptionValueQueryExecutionId: executionId } = props;
    const { currenCollectionId } = this.state;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      collectionTopFilterOptionValueQueryExecutionId: nextExecutionId
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: false,
      collectionId: currenCollectionId
    });

    // Update execution id
    if (nextExecutionId && executionId !== nextExecutionId) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.COLLECTION_TOP_FILTER_OPTION_VALUE,
        executionId: nextExecutionId
      });
    }
  };

  handleSelectCollection = collectionId => {
    this.setState({ currenCollectionId: collectionId });

    const { collectionTop, fromDate, toDate, compare } = this.props;
    const { perPage } = this.state;

    const hasNext = _.size(collectionTop) > perPage;
    this.setState({ rows: collectionTop, hasNext });

    if (fromDate && toDate) {
      // Get from date, to date with store timezone
      const fromDateUTC = getUTCDate(fromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(toDate, 24, 0, 0, 0);
      this.getCollectionTopFilterOptionValue(fromDateUTC, toDateUTC, compare, collectionId);
    }
  };

  render() {
    const {
      i18n,
      collectionTopFilterOptionValue,
      collectionTopFilterOptionValueLoading,
      openReviewAddonModal,
      performActionSurvey,
      collections
    } = this.props;

    const { page, directionSort, totalPage, currenCollectionId } = this.state;
    const options = this.buildOptions();
    const arrowSort = directionSort === SORT_VALUE.ASCENDING ? CaretUpMinor : CaretDownMinor;

    return (
      <div
        className="BC-Page BC-Page__CollectionAnalytics__FilterOptionValue"
        onScroll={this.handleScroll}>
        <Filter
          feature={FEATURES.COLLECTION}
          performActionSurvey={performActionSurvey}
          fromDateStateName={fromDateStateName}
          toDateStateName={toDateStateName}
          compareStateName={compareStateName}
          metric={COLLECTION_ANALYTIC_METRIC.FILTER_OPTION_VALUE}
          showCompare={false}
          openReviewAddonModal={openReviewAddonModal}
        />

        <div>
          <Card>
            <Card.Section>
              <div className="Polaris-Card__Header action-header">
                <Tooltip
                  preferredPosition="above"
                  content={i18n.translate(
                    'AdminHTML.Analytics.CollectionAnalytics.collection_Tooltip_TopFilterOptionValue_cardTitle'
                  )}>
                  <TextStyle>
                    <h2 className="Polaris-Heading">
                      {i18n.translate(
                        'AdminHTML.Analytics.CollectionAnalytics.collectionTopFilterOptionValue_cardTitle'
                      )}
                    </h2>
                  </TextStyle>
                </Tooltip>
                <CollectionAnalyticActionList
                  collections={collections}
                  handleSelectCollection={this.handleSelectCollection}
                  currenCollectionId={currenCollectionId}></CollectionAnalyticActionList>
              </div>
            </Card.Section>
            <DataTable
              columnContentTypes={['text', 'text', 'text', 'text']}
              sortable={[true, true, true, true]}
              onSort={this.handleSort}
              defaultSortDirection="descending"
              footerContent={
                _.isEmpty(collectionTopFilterOptionValue) && !collectionTopFilterOptionValueLoading
                  ? i18n.translate(
                      'AdminHTML.Analytics.CollectionAnalytics.collectionTopFilterOptionValue_noResult',
                      { count: 0 }
                    )
                  : ''
              }
              headings={[
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.CollectionAnalytics.collectionTopFilterOptionValue_filterOptionValueHeader'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>,
                <div className="header-with-sortable">
                  {i18n.translate(
                    'AdminHTML.Analytics.CollectionAnalytics.collectionTopFilterOptionValue_totalClicksHeader'
                  )}
                  <span className="Polaris-DataTable__Icon--custom">
                    <Icon source={arrowSort} color="base" />
                  </span>
                </div>
              ]}
              rows={options}
            />
          </Card>
          <div className="BC-Page__CollectionAnalytics__FilterOptionValue__Pagination">
            {!_.isEmpty(collectionTopFilterOptionValue) &&
              !collectionTopFilterOptionValueLoading && (
                <Pagination
                  label={`${page}/${totalPage}`}
                  hasPrevious={page > 1}
                  onPrevious={this.previousPage}
                  hasNext={page < totalPage}
                  onNext={this.nextPage}
                />
              )}
          </div>
        </div>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  changeValue: (name, value) => dispatch(AnalyticsActions.changeValue(name, value)),
  getCollectionTopFilterOptionValue: (
    fromDate,
    toDate,
    compare,
    queryExecutionId,
    top,
    collectionId
  ) =>
    dispatch(
      AnalyticsActions.getCollectionTopFilterOptionValue(
        fromDate,
        toDate,
        compare,
        queryExecutionId,
        top,
        ownProps.i18n,
        collectionId
      )
    ),
  sortData: (name, field, direction, fieldInsensitive = '') =>
    dispatch(AnalyticsActions.sortData(name, field, direction, fieldInsensitive)),

  updateExecutionId: data => dispatch(AnalyticsQueryExecutionIdActions.updateExecutionId(data))
});

const mapStateToProps = (state, props) => {
  return {
    fromDate: _.get(state.analyticsV2, fromDateStateName),
    toDate: _.get(state.analyticsV2, toDateStateName),
    compare: _.get(state.analyticsV2, compareStateName, false),

    // Query execution ids
    executionIds: _.get(state.analyticsQueryExecutionId, 'list', []),

    // Collection: top
    collectionTopFilterOptionValue: _.get(state.analyticsV2, 'collectionTopFilterOptionValue', []),
    collectionTopFilterOptionValueLoading: _.get(
      state.analyticsV2,
      'collectionTopFilterOptionValueLoading',
      false
    ),
    collectionTopFilterOptionValueQueryExecutionId: _.get(
      state.analyticsV2,
      'collectionTopFilterOptionValueQueryExecutionId'
    ),

    // List collections
    collections: _.get(state.analyticsV2, 'collections', [])
  };
};

export default withI18n()(connect(mapStateToProps, mapDispatchToProps)(FilterOptionValueAnalytics));
